import { z } from 'zod';

/** A Mac app the user let agents use without asking each time. */
export const computerUseAppSchema = z.object({
  bundleId: z.string(),
  name: z.string(),
  allowedAt: z.string(),
});

/**
 * What agents ask the user before doing in an app, even one allowed for good (Settings › Computer
 * use › Ask before): typing a password, sending a message or an email, buying anything.
 */
export const computerUseAskBeforeSchema = z.object({
  passwords: z.boolean(),
  messages: z.boolean(),
  purchases: z.boolean(),
});

export const DEFAULT_COMPUTER_USE_ASK_BEFORE = {
  passwords: true,
  messages: true,
  purchases: true,
} as const satisfies z.infer<typeof computerUseAskBeforeSchema>;

/** Computer use on the machine `crowdex serve` runs on (`GET /v1/computer`). */
export const computerUseStatusSchema = z.object({
  /** Only macOS has it. */
  supported: z.boolean(),
  enabled: z.boolean(),
  /** Cua Driver's version, `null` while it is not installed. */
  installed: z.string().nullable(),
  /** What macOS allows CuaDriver, `null` when unknown (not installed, or not answering). */
  permissions: z.object({ accessibility: z.boolean(), screenRecording: z.boolean() }).nullable(),
  /** What runs in the background: installing, or waiting for the user in System Settings. */
  busy: z.enum(['installing', 'granting', 'uninstalling']).nullable(),
  /** Why the last action failed. */
  error: z.string().nullable(),
  /** The allowed apps, with their icon (a PNG data URL, `null` when macOS has none). */
  apps: z.array(computerUseAppSchema.extend({ icon: z.string().nullable() })),
  askBefore: computerUseAskBeforeSchema.default(DEFAULT_COMPUTER_USE_ASK_BEFORE),
});

export const computerUseActionSchema = z.discriminatedUnion('action', [
  z.object({ action: z.enum(['install', 'grant', 'uninstall']) }),
  z.object({ action: z.literal('enable'), enabled: z.boolean() }),
  z.object({ action: z.literal('revoke'), bundleId: z.string().min(1).max(256) }),
  z.object({ action: z.literal('ask-before'), askBefore: computerUseAskBeforeSchema.partial() }),
]);

export type ComputerUseApp = z.infer<typeof computerUseAppSchema>;
export type ComputerUseStatus = z.infer<typeof computerUseStatusSchema>;
export type ComputerUseAskBefore = z.infer<typeof computerUseAskBeforeSchema>;
export type ComputerUseAction = z.infer<typeof computerUseActionSchema>;
